/* Elvora menu — hover mega menu (desktop) + thumbnails in the mobile drawer */
(() => {
  if (window.__elvoraMenuLoaded) return;
  window.__elvoraMenuLoaded = true;

  const OPEN_DELAY = 70;
  const CLOSE_DELAY = 220;

  const closeAll = (except) => {
    document.querySelectorAll('elvora-mega-menu').forEach((menu) => {
      if (menu !== except && typeof menu.close === 'function') menu.close();
    });
  };

  class ElvoraMegaMenu extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.trigger = this.querySelector('.elvora-menu__trigger');
      this.panel = this.querySelector('.elvora-menu__panel');
      this.cats = Array.from(this.querySelectorAll('.elvora-menu__cat'));
      this.views = Array.from(this.querySelectorAll('.elvora-menu__view'));
      this.timer = null;
      this.pointerType = '';

      // Mouse: open on hover, close shortly after leaving (the delay lets the pointer reach the panel)
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.schedule(true);
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.schedule(false);
      });

      // Touch / keyboard: toggle on tap or Enter
      this.trigger.addEventListener('pointerdown', (event) => { this.pointerType = event.pointerType; });
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.pointerType === 'mouse' && this.isOpen) { this.pointerType = ''; return; }
        this.pointerType = '';
        this.isOpen ? this.close() : this.open();
      });

      // Categories: hover / focus activates; on touch the first tap shows the category, the second one follows the link
      this.cats.forEach((cat) => {
        const link = cat.querySelector('a');
        const activate = () => this.activate(cat.dataset.target);
        cat.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') activate(); });
        link.addEventListener('focus', activate);
        link.addEventListener('pointerdown', (event) => { link.dataset.pointer = event.pointerType; });
        link.addEventListener('click', (event) => {
          const isTouch = link.dataset.pointer === 'touch' || link.dataset.pointer === 'pen';
          link.dataset.pointer = '';
          if (isTouch && !cat.classList.contains('is-active')) {
            event.preventDefault();
            activate();
          }
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      document.addEventListener('pointerdown', (event) => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      });
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    schedule(open) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => (open ? this.open() : this.close()), open ? OPEN_DELAY : CLOSE_DELAY);
    }

    open() {
      clearTimeout(this.timer);
      closeAll(this);
      this.setAttribute('data-open', '');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      clearTimeout(this.timer);
      this.removeAttribute('data-open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    activate(target) {
      this.cats.forEach((cat) => cat.classList.toggle('is-active', cat.dataset.target === String(target)));
      this.views.forEach((view) => view.classList.toggle('is-active', view.dataset.view === String(target)));
    }
  }

  if (!customElements.get('elvora-mega-menu')) customElements.define('elvora-mega-menu', ElvoraMegaMenu);

  // Close the mega menu when the sticky header slides away
  const watchHeader = () => {
    const header = document.querySelector('.section-header');
    if (!header || header.__elvoraWatched) return;
    header.__elvoraWatched = true;
    new MutationObserver(() => {
      if (header.classList.contains('shopify-section-header-hidden')) closeAll();
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  };

  // Mobile / tablet drawer: add product thumbnails to sub-levels, reusing the images rendered in the desktop panel
  const normalise = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const enhanceDrawer = () => {
    const drawer = document.querySelector('header-drawer');
    if (!drawer) return;

    const byHref = new Map();
    const byTitle = new Map();

    document.querySelectorAll('.elvora-menu__sublink, .elvora-menu__tile, .elvora-menu__hero').forEach((anchor) => {
      const img = anchor.querySelector('img');
      const src = img && (img.getAttribute('data-product-src') || img.getAttribute('src'));
      if (src && anchor.getAttribute('href')) byHref.set(anchor.getAttribute('href'), src);
    });
    document.querySelectorAll('.elvora-menu__cat').forEach((cat) => {
      const link = cat.querySelector('a');
      const src = cat.dataset.thumb;
      if (!link || !src) return;
      byTitle.set(normalise(link.textContent), src);
      byHref.set(link.getAttribute('href'), src);
    });

    drawer.querySelectorAll('.menu-drawer__submenu .menu-drawer__menu-item').forEach((item) => {
      if (item.classList.contains('has-thumb') || item.classList.contains('elvora-viewall')) return;
      const title = normalise(item.textContent);
      if (title === 'view all') {
        item.classList.add('elvora-viewall');
        return;
      }
      const href = item.getAttribute('href');
      const src = (href && byHref.get(href)) || byTitle.get(title);
      const thumb = document.createElement('span');
      thumb.className = 'elvora-thumb' + (src ? '' : ' elvora-thumb--empty');
      thumb.setAttribute('aria-hidden', 'true');
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.width = 240;
        img.height = 240;
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      item.classList.add('has-thumb');
      item.insertBefore(thumb, item.firstChild);
    });
  };

  const init = () => {
    watchHeader();
    enhanceDrawer();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
